import {
  ReactNode,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from 'react';
import { useNavigate } from 'react-router-dom';
import SCENE_DICTIONARY from '../../Scenes';
import { SceneRegister, Scene } from '../../Scenes/types';
import { Model } from '../useModelState';
import PCSpool from '../../MineModel/Spools/PCSpool';
import SceneManagerContext, { ISceneManagerContext } from './sceneManagerContext';

interface SceneManagerProviderProps {
  children: ReactNode;
  path: string;
  url: string;
}

const SceneManagerProvider = ({ children, path, url }: SceneManagerProviderProps) => {
  const navigate = useNavigate();
  const [currentScene, setCurrentScene] = useState<Scene>();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [shouldSaveView, setShouldSaveView] = useState(false);
  const [shouldLoadView, setShouldLoadView] = useState(false);
  const [shouldEditNode, setShouldEditNode] = useState(false);
  const [spoolToEdit, setSpoolToEdit] = useState<PCSpool | undefined>();
  const [addStopeMode, setAddStopeModeState] = useState(false);
  const [shouldAutoRecipeCreation, setShouldAutoRecipeCreation] = useState(false);
  const [shouldAutoRecipeList, setShouldAutoRecipeList] = useState(false);
  const [shouldAutoPourCreation, setShouldAutoPourCreation] = useState(false);
  const [shouldAutoPourList, setShouldAutoPourList] = useState(false);

  const sceneManagerRef = useRef<ISceneManagerContext | null>(null);

  const saveView = useCallback(() => setShouldSaveView(true), []);
  const loadView = useCallback(() => setShouldLoadView(true), []);
  const editNodes = useCallback((isOpen: boolean) => setShouldEditNode(isOpen), []);
  const editSpool = useCallback((spool: PCSpool | undefined) => setSpoolToEdit(spool), []);
  const setAddStopeMode = useCallback((mode: boolean) => setAddStopeModeState(mode), []);
  const setAutoRecipeCreationOpen = useCallback((isOpen: boolean) => setShouldAutoRecipeCreation(isOpen), []);
  const setAutoRecipeListOpen = useCallback((isOpen: boolean) => setShouldAutoRecipeList(isOpen), []);
  const setAutoPourCreationOpen = useCallback((isOpen: boolean) => setShouldAutoPourCreation(isOpen), []);
  const setAutoPourListOpen = useCallback((isOpen: boolean) => setShouldAutoPourList(isOpen), []);

  const setScene = useCallback((sceneKey: SceneRegister, model?: Model) => {
    const sceneBuilder = SCENE_DICTIONARY[sceneKey];
    const ctx = sceneManagerRef.current!;

    if (!ctx) {
      return;
    }

    const previousScene = ctx.state.currentScene;
    if (previousScene && previousScene.key !== sceneKey && previousScene.onExit && model) {
      previousScene.onExit(model);
    }

    const activeScene = sceneBuilder({
      key: sceneKey,
      meta: { path, url },
      context: ctx,
    });
    setCurrentScene(activeScene);
  }, [path, url]);

  const sceneActive = useCallback(() => currentScene !== undefined, [currentScene]);

  const display = useCallback(
    (model: Model) => currentScene && currentScene.display(model),
    [currentScene],
  );

  const sideDrawerContent = useCallback(
    (model: Model) => currentScene?.sideDrawerContent && currentScene.sideDrawerContent(model),
    [currentScene],
  );

  const topToolbarContent = useCallback(
    (model: Model) => currentScene?.topToolbarContent && currentScene.topToolbarContent(model),
    [currentScene],
  );

  const refreshScene = useCallback(() => {
    if (!currentScene?.key) return;
    setScene(currentScene.key);
  }, [currentScene, setScene]);

  const sceneManagerObj: ISceneManagerContext = useMemo(
    () => ({
      state: {
        currentScene,
        drawerOpen,
        shouldSaveView,
        shouldLoadView,
        shouldAutoRecipeCreation,
        shouldAutoRecipeList,
        shouldAutoPourCreation,
        shouldAutoPourList,
        shouldEditNode,
        spoolToEdit,
        addStopeMode,
      },
      actions: {
        setScene,
        sceneActive,
        display,
        sideDrawerContent,
        topToolbarContent,
        setDrawerOpen,
        saveView,
        loadView,
        editNodes,
        editSpool,
        setAddStopeMode,
        setAutoRecipeCreationOpen,
        setAutoRecipeListOpen,
        setAutoPourCreationOpen,
        setAutoPourListOpen,
        navigate,
        refreshScene,
      },
    }),
    [
      currentScene,
      drawerOpen,
      shouldSaveView,
      shouldLoadView,
      shouldAutoRecipeCreation,
      shouldAutoRecipeList,
      shouldAutoPourCreation,
      shouldAutoPourList,
      shouldEditNode,
      spoolToEdit,
      addStopeMode,
      setScene,
      sceneActive,
      display,
      sideDrawerContent,
      topToolbarContent,
      saveView,
      loadView,
      editNodes,
      editSpool,
      setAddStopeMode,
      setAutoRecipeCreationOpen,
      setAutoRecipeListOpen,
      setAutoPourCreationOpen,
      setAutoPourListOpen,
      navigate,
      refreshScene,
    ],
  );

  // Keep the ref pointing to the latest object (safe for callbacks)
  sceneManagerRef.current = sceneManagerObj;

  // Rebuild scene when drawerOpen toggles
  const previousDrawerOpenRef = useRef(drawerOpen);
  const currentSceneKeyRef = useRef<SceneRegister | undefined>(currentScene?.key);

  useEffect(() => {
    currentSceneKeyRef.current = currentScene?.key;
  }, [currentScene]);

  useEffect(() => {
    if (currentSceneKeyRef.current && previousDrawerOpenRef.current !== drawerOpen) {
      previousDrawerOpenRef.current = drawerOpen;
      setScene(currentSceneKeyRef.current);
    }
  }, [drawerOpen, setScene]);

  useEffect(() => {
    if (currentScene && shouldSaveView) {
      setShouldSaveView(false);
    }
  }, [currentScene, shouldSaveView]);

  useEffect(() => {
    if (currentScene && shouldLoadView) {
      setShouldLoadView(false);
    }
  }, [currentScene, shouldLoadView]);

  return (
    <SceneManagerContext.Provider value={sceneManagerObj}>
      {children}
    </SceneManagerContext.Provider>
  );
};

export default SceneManagerProvider;
